import React, { useState } from 'react';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebaseClient';
import './LoginPage.css';

type Step = 'login' | 'privacy' | 'register' | 'registerSuccess';

export default function LoginPage() {
  const [step, setStep] = useState<Step>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [privacyChecked, setPrivacyChecked] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err: any) {
      switch (err.code) {
        case 'auth/user-not-found': setError('등록되지 않은 이메일입니다.'); break;
        case 'auth/wrong-password': setError('비밀번호가 틀렸습니다.'); break;
        case 'auth/invalid-credential': setError('이메일 또는 비밀번호가 올바르지 않습니다.'); break;
        case 'auth/invalid-email': setError('올바른 이메일 형식이 아닙니다.'); break;
        default: setError('오류가 발생했습니다. 다시 시도해주세요.');
      }
    }
    setLoading(false);
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await createUserWithEmailAndPassword(auth, email, password);
      setStep('registerSuccess');
      setTimeout(() => {
        setEmail('');
        setPassword('');
        setStep('login');
      }, 2000);
    } catch (err: any) {
      switch (err.code) {
        case 'auth/email-already-in-use': setError('이미 사용 중인 이메일입니다.'); break;
        case 'auth/weak-password': setError('비밀번호는 6자 이상이어야 합니다.'); break;
        case 'auth/invalid-email': setError('올바른 이메일 형식이 아닙니다.'); break;
        default: setError('오류가 발생했습니다. 다시 시도해주세요.');
      }
    }
    setLoading(false);
  };

  // 개인정보 동의 화면
  const renderPrivacy = () => (
    <div className="login-box">
      <h2 className="login-box-title">개인정보 수집 및 이용 동의</h2>
      <div className="privacy-content">
        <p><strong>수집 항목:</strong> 이메일 주소</p>
        <p><strong>수집 목적:</strong> 회원 식별 및 로그인 서비스 제공</p>
        <p><strong>보유 기간:</strong> 회원 탈퇴 시까지</p>
        <p><strong>취급 방침:</strong> 수집된 정보는 제3자에게 제공되지 않습니다.</p>
      </div>
      <label className="privacy-check-label">
        <input
          type="checkbox"
          checked={privacyChecked}
          onChange={e => setPrivacyChecked(e.target.checked)}
        />
        &nbsp; 개인정보 수집 및 이용에 동의합니다.
      </label>
      <button
        className="login-submit-btn"
        disabled={!privacyChecked}
        onClick={() => { setStep('register'); }}
      >
        다음
      </button>
      <button
        className="login-back-btn"
        onClick={() => { setStep('login'); setPrivacyChecked(false); }}
      >
        뒤로
      </button>
    </div>
  );

  // 회원가입 완료 화면
  const renderSuccess = () => (
    <div className="login-box">
      <div className="login-success-icon">🎉</div>
      <h2 className="login-box-title">회원가입 완료!</h2>
      <p className="login-box-sub">잠시 후 로그인 화면으로 이동합니다...</p>
    </div>
  );

  // 로그인 / 회원가입 폼
  const renderForm = () => (
    <div className="login-box">
      <h2 className="login-box-title">
        {step === 'login' ? '로그인' : '회원가입'}
      </h2>
      <p className="login-box-sub">
        {step === 'login' ? '계정에 로그인하세요' : '새 계정을 만드세요'}
      </p>

      <form onSubmit={step === 'login' ? handleLogin : handleRegister}>
        <div className="login-form-group">
          <label>이메일</label>
          <input
            type="email"
            placeholder="이메일 입력"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
          />
        </div>
        <div className="login-form-group">
          <label>비밀번호</label>
          <input
            type="password"
            placeholder="비밀번호 입력 (6자 이상)"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
          />
        </div>

        {error && <div className="login-error">{error}</div>}

        <button type="submit" className="login-submit-btn" disabled={loading}>
          {loading ? '처리 중...' : (step === 'login' ? '로그인' : '회원가입')}
        </button>
      </form>

      <div className="login-switch">
        {step === 'login' ? '계정이 없으신가요?' : '이미 계정이 있으신가요?'}
        <button onClick={() => {
          if (step === 'login') {
            setStep('privacy');
          } else {
            setStep('login');
          }
          setError('');
        }}>
          {step === 'login' ? ' 회원가입' : ' 로그인'}
        </button>
      </div>
    </div>
  );

  return (
    <div className="login-container">
      <div className="login-left">
        <div className="login-brand">
          <div className="login-logo">💳</div>
          <h1 className="login-title">소상공인 계좌QR</h1>
          <p className="login-desc">수수료 0원 QR 계좌이체 솔루션</p>
          <div className="login-features">
            <div className="login-feature-item">✅ 수수료 0원</div>
            <div className="login-feature-item">✅ 앱 설치 불필요</div>
            <div className="login-feature-item">✅ 브랜드 QR 커스터마이징</div>
            <div className="login-feature-item">✅ 계좌번호 자동 복사</div>
          </div>
        </div>
      </div>
      <div className="login-right">
        {step === 'privacy' && renderPrivacy()}
        {step === 'registerSuccess' && renderSuccess()}
        {(step === 'login' || step === 'register') && renderForm()}
      </div>
    </div>
  );
}
